import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import PeopleIcon from '@mui/icons-material/People';
import MapIcon from '@mui/icons-material/Map';
import { NumericField, OtaDatePicker, OtaFooter, formatTime } from '@ota/design-system';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { PublicCategoryAvailability } from '@ota/api-client';
import { fetchAvailability, fetchProperty } from '../api';
import { PublicHeader } from '../components/PublicHeader';
import { ReviewsSection } from '../components/ReviewsSection';
import { CancellationPolicyInfo } from '../components/CancellationPolicyInfo';

/**
 * Shows a single image when only one exists, or a carousel with prev/next
 * arrow buttons and dot indicators when multiple images are present.
 */
function RoomImageCarousel({ imageIds }: { imageIds: number[] }) {
  const [active, setActive] = useState(0);

  const prev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActive((i) => (i === 0 ? imageIds.length - 1 : i - 1));
  };
  const next = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActive((i) => (i === imageIds.length - 1 ? 0 : i + 1));
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: 180, sm: '100%' },
        minHeight: { sm: 160 },
        overflow: 'hidden',
        borderRadius: { xs: '12px 12px 0 0', sm: '12px 0 0 12px' },
      }}
    >
      <Box
        component="img"
        src={`/api/media/${imageIds[active]}`}
        alt={`Room photo ${active + 1}`}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />

      {imageIds.length > 1 && (
        <>
          {/* Prev arrow */}
          <IconButton
            size="small"
            onClick={prev}
            sx={{
              position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)',
              bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', p: 0.5,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>

          {/* Next arrow */}
          <IconButton
            size="small"
            onClick={next}
            sx={{
              position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
              bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', p: 0.5,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>

          {/* Dot indicators */}
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              position: 'absolute', bottom: 7, left: '50%', transform: 'translateX(-50%)',
              pointerEvents: 'none',
            }}
          >
            {imageIds.map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: i === active ? 14 : 6,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: i === active ? '#fff' : 'rgba(255,255,255,0.55)',
                  transition: 'width 0.2s, background-color 0.2s',
                }}
              />
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function tomorrowISO() {
  return addDaysISO(todayISO(), 1);
}
function addDaysISO(iso: string, days: number) {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function PropertyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const propertyId = Number(id);
  const navigate = useNavigate();
  const [search, setSearchParams] = useSearchParams();

  const [checkIn, setCheckIn] = useState(search.get('checkIn') || todayISO());
  const [checkOut, setCheckOut] = useState(search.get('checkOut') || tomorrowISO());
  // Kept as a string while typing (mirrors the "roomsCount" pattern on CheckoutPage)
  // so the field can be briefly empty mid-edit instead of snapping to a number.
  const [guestsInput, setGuestsInput] = useState(String(Number(search.get('guests') ?? '2') || 2));
  const guests = Math.max(1, Number(guestsInput) || 1);
  const [activeImage, setActiveImage] = useState(0);
  const [bookingKey, setBookingKey] = useState<string | null>(null);

  const datesValid = checkOut > checkIn;

  const handleCheckInChange = (v: string) => {
    setCheckIn(v);
    // Auto-advance check-out by a night if it's no longer after the new check-in,
    // so the room list doesn't silently go blank on an invalid range.
    if (checkOut <= v) setCheckOut(addDaysISO(v, 1));
  };

  // Keep the URL in sync so a refresh/share preserves the dates + guest count
  // the user picked here (same "URL-driven state" convention used on ListingPage).
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('checkIn', checkIn);
        next.set('checkOut', checkOut);
        next.set('guests', String(guests));
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIn, checkOut, guests]);

  const property = useQuery({
    queryKey: ['property', propertyId],
    queryFn: () => fetchProperty(propertyId),
    enabled: !Number.isNaN(propertyId),
  });

  const availability = useQuery({
    queryKey: ['availability', propertyId, checkIn, checkOut],
    queryFn: () => fetchAvailability(propertyId, checkIn, checkOut),
    enabled: !Number.isNaN(propertyId) && !!checkIn && !!checkOut && checkOut > checkIn,
  });

  useEffect(() => { setActiveImage(0); }, [propertyId]);

  const onBook = (cat: PublicCategoryAvailability, mealPlanId: number | null, roomsNeeded: number) => {
    const key = `${cat.roomCategoryId}-${mealPlanId ?? 'none'}`;
    setBookingKey(key);
    const params = new URLSearchParams({
      propertyId: String(propertyId),
      roomCategoryId: String(cat.roomCategoryId),
      checkIn,
      checkOut,
      guests: String(guests),
      // Pre-fill checkout's room count so a party bigger than one room's max
      // occupancy lands on the right number of rooms instead of always "1".
      roomsCount: String(Math.min(roomsNeeded, cat.remainingCount)),
    });
    if (mealPlanId != null) params.set('mealPlanId', String(mealPlanId));
    navigate(`/checkout?${params.toString()}`);
  };

  const mapsUrl = useMemo(() => {
    if (!property.data?.latitude || !property.data?.longitude) return null;
    return `https://www.google.com/maps?q=${property.data.latitude},${property.data.longitude}&hl=en&z=15&output=embed`;
  }, [property.data?.latitude, property.data?.longitude]);

  if (property.isLoading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <PublicHeader />
        <Container sx={{ py: 4 }}><CircularProgress /></Container>
      </Box>
    );
  }
  if (!property.data) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <PublicHeader />
        <Container sx={{ py: 4 }}><Alert severity="error">Property not found.</Alert></Container>
      </Box>
    );
  }

  const p = property.data;
  const images = p.imageIds ?? [];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader />
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 }, flex: 1 }}>
        <Stack spacing={3}>
          {/* Back button */}
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ color: 'text.secondary', fontWeight: 500 }}
            >
              Back to results
            </Button>
          </Box>

          {/* Title */}
          <Stack spacing={1}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h2" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 700 }}>
                {p.name}
              </Typography>
              {p.starRating != null && (
                <Stack direction="row" alignItems="center" spacing={0.25}
                  sx={{ px: 1.25, py: 0.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                  <Rating value={p.starRating} max={5} readOnly size="small" />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', ml: 0.5 }}>
                    {p.starRating}-star
                  </Typography>
                </Stack>
              )}
              {p.propertyTypeCode && (
                <Chip label={p.propertyTypeCode} size="small" variant="outlined" />
              )}
              {p.avgRating !== undefined && p.avgRating !== null && p.reviewCount > 0 && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      px: 1.25, py: 0.5,
                      borderRadius: 1,
                      bgcolor: 'success.main',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.95rem',
                    }}
                  >
                    {p.avgRating.toFixed(1)}
                  </Box>
                  <Stack>
                    <Rating value={p.avgRating} precision={0.1} readOnly size="small" />
                    <Typography variant="caption" color="text.secondary">
                      {p.reviewCount} review{p.reviewCount === 1 ? '' : 's'}
                    </Typography>
                  </Stack>
                </Stack>
              )}
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center" color="text.secondary">
              <LocationOnIcon fontSize="small" />
              <Typography variant="body2">
                {[p.addressLine1, p.area, p.city, p.state].filter(Boolean).join(', ')}
              </Typography>
            </Stack>
            {p.landmark && (
              <Stack direction="row" spacing={0.75} alignItems="center" color="text.secondary">
                <LocationOnIcon fontSize="small" sx={{ opacity: 0.6 }} />
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  {p.landmark}
                </Typography>
              </Stack>
            )}
          </Stack>

          {/* Image gallery */}
          {images.length > 0 ? (
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={8}>
                <Box
                  component="img"
                  src={`/api/media/${images[activeImage]}`}
                  alt={p.name}
                  sx={{
                    width: '100%', height: { xs: 280, md: 460 },
                    objectFit: 'cover', borderRadius: 3,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Grid container spacing={1.5}>
                  {images.slice(0, 4).map((id, i) => (
                    <Grid item xs={6} key={id}>
                      <Box
                        component="img"
                        src={`/api/media/${id}`}
                        alt={`thumb-${i}`}
                        onClick={() => setActiveImage(i)}
                        sx={{
                          width: '100%', height: { xs: 100, md: 110 },
                          objectFit: 'cover', borderRadius: 2,
                          cursor: 'pointer',
                          border: 2,
                          borderColor: i === activeImage ? 'primary.main' : 'transparent',
                          '&:hover': { borderColor: 'primary.light' },
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{
              height: 280, bgcolor: 'background.subtle', borderRadius: 3,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'text.disabled',
            }}>
              No photos uploaded yet
            </Box>
          )}

          {/* Description + amenities + map */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h5" sx={{ mb: 1.5 }}>About this property</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }} color="text.secondary">
                    {p.description || 'No description provided.'}
                  </Typography>

                  {(p.checkInTime || p.checkOutTime) && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                        {p.checkInTime && (
                          <Stack spacing={0.25}>
                            <Typography variant="caption" color="text.secondary"
                              sx={{ textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600 }}>
                              Check-in
                            </Typography>
                            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                              {formatTime(p.checkInTime)}
                            </Typography>
                          </Stack>
                        )}
                        {p.checkOutTime && (
                          <Stack spacing={0.25}>
                            <Typography variant="caption" color="text.secondary"
                              sx={{ textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600 }}>
                              Check-out
                            </Typography>
                            <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 700 }}>
                              {formatTime(p.checkOutTime)}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                    </>
                  )}

                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h5" sx={{ mb: 1.5 }}>Amenities</Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                    {p.amenities.length === 0
                      ? <Typography variant="body2" color="text.disabled">None listed.</Typography>
                      : p.amenities.map((a) => (
                          <Chip key={a.code} label={a.label} variant="outlined" />
                        ))}
                  </Stack>

                  {p.houseRules && p.houseRules.length > 0 && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Typography variant="h5" sx={{ mb: 1.5 }}>House Rules</Typography>
                      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                        {p.houseRules.map((r) => (
                          <Chip
                            key={r.code}
                            label={r.label}
                            variant="outlined"
                            color="secondary"
                            size="small"
                          />
                        ))}
                      </Stack>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <MapIcon color="primary" />
                    <Typography variant="h6">Location</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {[p.addressLine1, p.addressLine2, p.area, p.city, p.state, p.postalCode]
                      .filter(Boolean).join(', ')}
                  </Typography>
                  {mapsUrl ? (
                    <Box
                      component="iframe"
                      src={mapsUrl}
                      sx={{ width: '100%', height: 220, border: 0, borderRadius: 2 }}
                      loading="lazy"
                      allowFullScreen
                    />
                  ) : (
                    <Box sx={{
                      height: 220, bgcolor: 'background.subtle', borderRadius: 2,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'text.disabled', fontSize: 14, textAlign: 'center', px: 2,
                    }}>
                      Map unavailable — coordinates not set on this property.
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Date picker + room categories */}
          <Card>
            <CardContent>
              <Typography variant="h5" sx={{ mb: 2 }}>Available rooms</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={3}>
                  <OtaDatePicker label="Check-in" fullWidth
                    value={checkIn} onChange={handleCheckInChange} min={todayISO()} size='small'/>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <OtaDatePicker label="Check-out" fullWidth
                    value={checkOut}
                    onChange={setCheckOut}
                    min={addDaysISO(checkIn, 1)}
                    size='small'/>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <NumericField label="Guests" fullWidth
                    value={guestsInput}
                    onChange={(e) => setGuestsInput(e.target.value)}
                    onBlur={() => setGuestsInput(String(guests))}
                    size='small'/>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Stack alignItems={{ xs: 'stretch', sm: 'flex-end' }} sx={{ height: '100%' }}>
                    <Typography variant="caption" color="text.secondary">
                      {datesValid && availability.data ? `${availability.data.nightsCount} nights` : '—'}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              {!datesValid && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Check-out date must be after check-in date. Please pick valid dates.
                </Alert>
              )}
              {datesValid && availability.isLoading && <CircularProgress size={24} />}
              {datesValid && availability.isError && (
                <Alert severity="error">Couldn't load availability for those dates.</Alert>
              )}

              {availability.data?.categories.length === 0 && (
                <Alert severity="info">No room categories defined yet for this property.</Alert>
              )}

              <Stack spacing={2}>
                {(availability.data?.categories ?? []).map((c) => {
                  const nights = availability.data!.nightsCount;
                  const baseRate = Number(c.startingPricePerNight);
                  // Occupancy check: maxOccupancy is per room, guests is the total
                  // party size picked above. A party larger than one room's max
                  // needs multiple rooms of this category.
                  const roomsNeeded = Math.max(1, Math.ceil(guests / c.maxOccupancy));
                  const fitsInOneRoom = guests <= c.maxOccupancy;
                  const enoughRoomsForParty = roomsNeeded <= c.remainingCount;
                  const canBook = c.remainingCount > 0 && enoughRoomsForParty;
                  return (
                    <Card key={c.roomCategoryId} variant="outlined">
                      <Grid container>
                        {c.imageIds.length > 0 && (
                          <Grid item xs={12} sm={3}>
                            <RoomImageCarousel imageIds={c.imageIds} />
                          </Grid>
                        )}
                        <Grid item xs={12} sm={c.imageIds.length > 0 ? 9 : 12}>
                          <CardContent>
                            {/* Category header */}
                            <Stack spacing={1} sx={{ mb: 2 }}>
                              <Typography variant="h6">{c.name}</Typography>
                              {c.description && (
                                <Typography variant="body2" color="text.secondary">{c.description}</Typography>
                              )}
                              <Stack direction="row" spacing={2} sx={{ color: 'text.secondary' }}>
                                {c.roomAreaSqft && (
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <SquareFootIcon fontSize="small" />
                                    <Typography variant="body2">{c.roomAreaSqft} sqft</Typography>
                                  </Stack>
                                )}
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <PeopleIcon fontSize="small" />
                                  <Typography variant="body2">{c.baseOccupancy}–{c.maxOccupancy} guests</Typography>
                                </Stack>
                              </Stack>
                              <Typography variant="caption" color={c.remainingCount > 0 ? 'success.main' : 'error.main'}>
                                {c.remainingCount > 0
                                  ? `${c.remainingCount} room${c.remainingCount === 1 ? '' : 's'} left`
                                  : 'Sold out for these dates'}
                              </Typography>
                              {c.remainingCount > 0 && !fitsInOneRoom && (
                                <Typography
                                  variant="caption"
                                  color={enoughRoomsForParty ? 'warning.main' : 'error.main'}
                                  sx={{ fontWeight: 600 }}
                                >
                                  {enoughRoomsForParty
                                    ? `Fits ${c.maxOccupancy} guests/room — you'll need ${roomsNeeded} rooms for ${guests} guests`
                                    : `Only ${c.remainingCount} room${c.remainingCount === 1 ? '' : 's'} left (fits ${c.remainingCount * c.maxOccupancy} guests max) — not enough for ${guests} guests`}
                                </Typography>
                              )}
                            </Stack>

                            <Divider sx={{ mb: 1.5 }} />

                            {/* One row per meal plan (or a single row when no plans are defined) */}
                            <Stack spacing={1}>
                              {c.mealPlans.length > 0 ? (
                                c.mealPlans.map((plan) => {
                                  const supplement = Number(plan.supplement);
                                  const pricePerNight = baseRate + supplement;
                                  return (
                                    <Box
                                      key={plan.id}
                                      sx={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        gap: 2, py: 1.25, px: 1.5, borderRadius: 1,
                                        border: '1px solid', borderColor: 'divider',
                                        bgcolor: 'background.default',
                                      }}
                                    >
                                      <Stack spacing={0.25} flex={1}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          <Box component="span" sx={{ color: 'primary.main', mr: 0.5 }}>
                                            {plan.code}
                                          </Box>
                                          · {plan.name}
                                        </Typography>
                                        {plan.description && (
                                          <Typography variant="caption" color="text.secondary">
                                            {plan.description}
                                          </Typography>
                                        )}
                                        {supplement > 0 && (
                                          <Typography variant="caption" color="text.disabled">
                                            Room ₹{baseRate.toLocaleString('en-IN')} + Meals ₹{supplement.toLocaleString('en-IN')}
                                          </Typography>
                                        )}
                                      </Stack>
                                      <Stack alignItems="flex-end" spacing={0.25} sx={{ minWidth: 160, flexShrink: 0 }}>
                                        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                                          ₹{pricePerNight.toLocaleString('en-IN')}
                                          <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 400 }}>
                                            /night
                                          </Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          ₹{(pricePerNight * nights).toLocaleString('en-IN')} for {nights} night{nights === 1 ? '' : 's'}
                                        </Typography>
                                        <Button
                                          variant="contained" color="secondary" size="small"
                                          disabled={!canBook || bookingKey !== null}
                                          startIcon={
                                            bookingKey === `${c.roomCategoryId}-${plan.id ?? 'none'}`
                                              ? <CircularProgress size={14} color="inherit" />
                                              : undefined
                                          }
                                          onClick={() => onBook(c, plan.id ?? null, roomsNeeded)}
                                          sx={{ mt: 0.5 }}
                                        >
                                          Book now
                                        </Button>
                                      </Stack>
                                    </Box>
                                  );
                                })
                              ) : (
                                // No meal plans — show base room rate
                                <Box
                                  sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    gap: 2,
                                  }}
                                >
                                  <Typography variant="body2" color="text.secondary">Room only</Typography>
                                  <Stack alignItems="flex-end" spacing={0.25} sx={{ minWidth: 160, flexShrink: 0 }}>
                                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                                      ₹{baseRate.toLocaleString('en-IN')}
                                      <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 400 }}>
                                        /night
                                      </Typography>
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      ₹{Number(c.totalForStay).toLocaleString('en-IN')} for {nights} night{nights === 1 ? '' : 's'}
                                    </Typography>
                                    <Button
                                      variant="contained" color="secondary" size="small"
                                      disabled={!canBook || bookingKey !== null}
                                      startIcon={
                                        bookingKey === `${c.roomCategoryId}-none`
                                          ? <CircularProgress size={14} color="inherit" />
                                          : undefined
                                      }
                                      onClick={() => onBook(c, null, roomsNeeded)}
                                      sx={{ mt: 0.5 }}
                                    >
                                      Book now
                                    </Button>
                                  </Stack>
                                </Box>
                              )}
                            </Stack>
                          </CardContent>
                        </Grid>
                      </Grid>
                    </Card>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>

          {/* Cancellation policy */}
          <Card>
            <CardContent>
              <Typography variant="h5" sx={{ mb: 1.5 }}>Cancellation policy</Typography>
              <CancellationPolicyInfo policyId={p.cancellationPolicyId} variant="full" />
            </CardContent>
          </Card>

          {/* Reviews */}
          <ReviewsSection
            propertyId={propertyId}
            avgRating={p.avgRating ?? undefined}
            reviewCount={p.reviewCount ?? 0}
          />
        </Stack>
      </Container>

      {/* Full-screen loader — blocks navigation while routing to checkout */}
      <Backdrop
        open={bookingKey !== null}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1, color: '#fff', flexDirection: 'column', gap: 2 }}
      >
        <CircularProgress color="inherit" size={56} />
        <Typography variant="h6" sx={{ color: '#fff' }}>Opening checkout…</Typography>
      </Backdrop>

      <OtaFooter />
    </Box>
  );
}
